// bk18 Mavjli Gulshan — pool-party birthday preview card (coral + cream).
//
// The catalog card uses the standard "outer frame + inner card" rhythm:
// a coral→cream sun-burst gradient frame surrounds the cream inner card.
// The focal element is a circular medallion holding a stylised coral
// branch motif, which keeps the original template's signature without
// becoming visual noise. Names + age in Cormorant Garamond.
//
// names = "Celebrant · Age" (mid-dot canonical, ampersand as legacy fallback).
import { type LayoutCtx } from "./_shared";

// Sunset coral wash for the outer paper.
const CORAL_GRADIENT =
  "linear-gradient(135deg, #FBEAE5 0%, #F2C2B2 22%, #ECA98F 50%, #F2C2B2 78%, #FBEAE5 100%)";

export function BookieCoralPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const parts = names.split(/\s*[·&]\s*/);
  const celebrant = parts[0] ?? names;
  const ageOrSecond = parts[1] ?? "";
  const captionDate = formatDate(date);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: CORAL_GRADIENT,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TUG'ILGAN KUN
        </div>

        {/* Coral-branch medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              background: CORAL_GRADIENT,
              padding: "5%",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                background: bg,
                display: "grid",
                placeItems: "center",
                boxShadow: `inset 0 0 0 1px ${accent}44`,
              }}
            >
              <svg
                viewBox="0 0 100 100"
                style={{ width: "70%", height: "70%", color: accent }}
                aria-hidden
              >
                {/* Central stem */}
                <path d="M50 96 Q 49 70 50 50 Q 51 30 50 14 Q 49.5 8 51 4"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" />
                {/* Side branches */}
                <path d="M50 72 Q 36 66 26 56" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
                <path d="M50 72 Q 64 66 74 56" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" fill="none" />
                <path d="M50 52 Q 40 46 32 38" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
                <path d="M50 52 Q 60 46 68 38" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
                <path d="M50 30 Q 42 24 36 18" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" fill="none" />
                <path d="M50 30 Q 58 24 64 18" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" fill="none" />
                {/* Tips */}
                <circle cx="26" cy="56" r="1.6" fill="currentColor" />
                <circle cx="74" cy="56" r="1.6" fill="currentColor" />
                <circle cx="32" cy="38" r="1.4" fill="currentColor" />
                <circle cx="68" cy="38" r="1.4" fill="currentColor" />
                <circle cx="36" cy="18" r="1.2" fill="currentColor" />
                <circle cx="64" cy="18" r="1.2" fill="currentColor" />
                <circle cx="51" cy="4" r="1.6" fill="currentColor" />
              </svg>
            </div>
          </div>
        </div>

        {/* Celebrant name */}
        <div style={{ fontFamily: font, fontStyle: "italic", fontSize: 26, lineHeight: 1.05, color: ink }}>
          {celebrant}
        </div>
        {ageOrSecond ? (
          <div style={{ fontFamily: font, fontSize: 20, color: accent, marginTop: 2, lineHeight: 1 }}>
            · {ageOrSecond} ·
          </div>
        ) : null}

        <div style={{ flex: 1 }} />

        {/* Caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          POOL PARTY · {captionDate}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function formatDate(d: string): string {
  const m = d.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return d.toUpperCase();
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const year = m[3];
  const months = [
    "YANVAR", "FEVRAL", "MART", "APREL", "MAY", "IYUN",
    "IYUL", "AVGUST", "SENTABR", "OKTABR", "NOYABR", "DEKABR",
  ];
  return `${day} ${months[month - 1] ?? ""} ${year}`;
}
